import { FormGroup, FormRadioGroup } from '@snsw/react-component-library';
import { AutoSuggestAddressEntryMode, AutoSuggestAddressModel } from 'components/Address/AutoSuggestAddress/types';
import { GelTheme } from 'components/GelTheme';
import { FlexBox } from 'components/Layout';
import { useOptionallyControlledValue } from 'components/Utils/useOptionallyControlledValue';
import React, { ReactNode, useRef } from 'react';
import styled from 'styled-components';
import { AutoSuggestAddress, AutoSuggestAddressProps } from './AutoSuggestAddress';

type AddressProps = Pick<
  AutoSuggestAddressProps,
  'label' | 'helpMessage' | 'value' | 'onChange' | 'onSelect' | 'onBlur' | 'onEntryModeChange' | 'initialMode'
>;

type Props = Pick<
  AutoSuggestAddressProps,
  'id' | 'api' | 'initialMode' | 'addressType' | 'debounceMillis' | 'manualAddressProps'
> & {
  /** Set the default value for the 'Same' property, if you do not want to control this value */
  defaultSame?: boolean;

  /** Controlled value for 'Same' */
  same?: boolean;

  /** Callback notified when the user changes the 'Same' property */
  onSameChange?: (same: boolean) => void;

  primaryAddressProps: AddressProps;
  secondaryAddressProps: AddressProps;
};

const RadioButtons = styled(FormRadioGroup)`
  margin-top: ${GelTheme.spacing(0)};
`;

const SameAsLabel = ({ children }: { children: ReactNode }) => {
  return typeof children === 'string' ? <>Same as {children.toLowerCase()}</> : <>Same as {children}</>;
};

export const AutoSuggestAddresses = ({
  id,
  defaultSame,
  same,
  onSameChange,
  primaryAddressProps,
  secondaryAddressProps,
  ...autoSuggestAddressProps
}: Props) => {
  const {
    label: secondaryAddressLabel,
    value: secondaryAddress,
    onChange: onSecondaryAddressChange,
    ...secondaryAddressPropsRest
  } = secondaryAddressProps;

  // We need to keep refs for various settings for the secondary address to cater for it unmounting/remounting
  const secondaryAddressRef = useRef(secondaryAddress);
  const secondaryEntryModeRef = useRef(
    secondaryAddressProps.initialMode ?? autoSuggestAddressProps.initialMode ?? 'auto'
  );
  const handleSecondaryEntryModeChange = (entryMode: AutoSuggestAddressEntryMode) => {
    secondaryEntryModeRef.current = entryMode;

    if (secondaryAddressProps.onEntryModeChange != null) {
      secondaryAddressProps.onEntryModeChange(entryMode);
    }
  };

  const [areSame, setAreSame] = useOptionallyControlledValue(
    same ?? defaultSame ?? (!secondaryAddressProps || primaryAddressProps.value === secondaryAddressProps.value),
    same,
    onSameChange
  );

  const handleAreSameChange = () => {
    const nextAreSame = !areSame;

    setAreSame(nextAreSame);
    onSecondaryAddressChange(nextAreSame ? undefined : secondaryAddressRef.current);
  };

  const handleSecondaryAddressChange = (next: AutoSuggestAddressModel) => {
    secondaryAddressRef.current = next;
    onSecondaryAddressChange(next);
  };

  return (
    <FlexBox direction="column" gap={1}>
      <AutoSuggestAddress id={`${id}-primary`} {...autoSuggestAddressProps} {...primaryAddressProps} />

      <FormGroup id={`${id}-secondary-form-group`} label={secondaryAddressLabel ?? ''}>
        <RadioButtons
          vertical
          legend=""
          options={[
            { label: <SameAsLabel>{primaryAddressProps.label}</SameAsLabel>, value: true },
            { label: 'Use a different address', value: false },
          ]}
          value={areSame}
          onChange={handleAreSameChange}
        />
      </FormGroup>

      {!areSame && (
        <AutoSuggestAddress
          id={`${id}-secondary`}
          {...autoSuggestAddressProps}
          {...secondaryAddressPropsRest}
          label=""
          initialMode={secondaryEntryModeRef.current}
          onEntryModeChange={handleSecondaryEntryModeChange}
          value={secondaryAddress}
          onChange={handleSecondaryAddressChange}
        />
      )}
    </FlexBox>
  );
};
